<template>
  <div class="qvp-kids-final-page">
    <div class="page-wrapper">
      <header class="page-header">
        <div class="page-header-bar">
          <div class="page-header-top">
            <span class="page-header-badge mal-text">ഫലം</span>
            <div class="qvp-logo-wrap">
              <img class="qvp-logo-corner" :src="qvpLogoUrl" alt="QVP" @error="handleQvpLogoError" />
            </div>
          </div>
          <div class="page-header-brand">
            <div class="kids-logo-frame">
              <img :src="kidsLogoUrl" alt="QVP Kids" class="kids-logo" @error="handleKidsLogoError" />
            </div>
          </div>
          <p class="page-header-subtitle mal-text">30-ാമത് ക്വുർആൻ വിജ്ഞാന പരീക്ഷ</p>
        </div>
      </header>

      <div class="main-card" :class="{ 'main-card--results': currentStep === 'results' }">
        <div class="alert-box" :class="alertClass" v-if="alertMessage">{{ alertMessage }}</div>

        <!-- Mobile lookup -->
        <div class="step-panel" v-show="currentStep === 'mobile'">
          <form class="lookup-form" @submit.prevent="handleMobileSubmit">
            <div class="mobile-row">
              <div class="form-group code-group">
                <label class="form-label mal-text">Code <span class="req">*</span></label>
                <select v-model="mobileCountryCode" class="form-input" required>
                  <option v-for="code in countryCodeList" :key="code.value" :value="code.value">
                    {{ code.label }}
                  </option>
                </select>
              </div>
              <div class="form-group mobile-group">
                <label class="form-label mal-text">Mobile Number <span class="req">*</span></label>
                <input
                  type="tel"
                  v-model="mobileInput"
                  class="form-input"
                  :placeholder="mobileCountryCode === '91' ? '10 Digit Number' : 'Enter Mobile Number'"
                  :maxlength="mobileCountryCode === '91' ? 10 : 15"
                  inputmode="numeric"
                  required
                />
              </div>
            </div>
            <button type="submit" class="btn-main" :disabled="loading">
              <span v-if="!loading">View Result</span>
              <span v-else class="loading-spinner"></span>
            </button>
            <p class="hint mal-text">Enter your registered mobile number</p>
          </form>
        </div>

        <!-- Results -->
        <div class="step-panel" v-show="currentStep === 'results'">
          <div class="active-mobile-card">
            <span class="number">+{{ mobileCountryCode }} {{ currentMobile }}</span>
            <button type="button" class="link-btn mal-text" @click="resetFlow">മാറ്റുക / Change</button>
          </div>

          <div v-if="loadingResults" class="loading-block">
            <div class="loading-spinner large"></div>
            <p class="mal-text">ഫലം ലോഡ് ചെയ്യുന്നു...</p>
          </div>

          <template v-else>
            <div v-if="kidsWithResults.length" class="result-cards">
              <article
                v-for="(kid, index) in kidsWithResults"
                :key="kid.id || index"
                class="share-card"
              >
                <div class="share-card-header">
                  <h3 class="exam-title">Final Exam Result</h3>
                </div>

                <div class="share-card-body">
                  <div class="share-student">
                    <div class="share-avatar">{{ getInitials(kid.name) }}</div>
                    <div class="share-student-info">
                      <h2 class="share-name">{{ kid.name }}</h2>
                      <p v-if="registrationNo(kid)" class="share-meta">Reg. No: {{ registrationNo(kid) }}</p>
                      <p v-if="kid.place" class="share-meta">{{ kid.place }}</p>
                    </div>
                  </div>

                  <div v-if="hasFinalMark(kid)" class="score-section">
                    <div class="score-section-main" :class="{ 'score-section-main--solo': !hasFinalRank(kid) }">
                      <div class="mark-ring">
                        <div class="mark-ring-score-wrap">
                          <span class="mark-ring-score">{{ kid.qvp_final_exam_mark }}</span>
                          <span class="mark-ring-max">/{{ finalExamMax }}</span>
                        </div>
                        <span class="mark-ring-label">Final Exam Mark</span>
                      </div>
                      <div
                        v-if="hasFinalRank(kid)"
                        class="rank-pill"
                        :class="'rank-pill--' + getRankTier(getFinalRank(kid))"
                        role="status"
                        :aria-label="'Rank ' + formatRank(getFinalRank(kid))"
                      >
                        <span class="rank-pill-icon" aria-hidden="true">{{ getRankMedal(getFinalRank(kid)) }}</span>
                        <span class="rank-pill-label">Rank</span>
                        <span class="rank-pill-value">{{ formatRank(getFinalRank(kid)) }}</span>
                      </div>
                    </div>
                  </div>

                  <div v-if="hasFirstExamMark(kid)" class="score-row score-row--prelim">
                    <span class="score-row-label">Preliminary Exam Mark</span>
                    <span class="score-row-value">{{ formatMarkScore(firstExamMark(kid), preliminaryExamMax) }}</span>
                  </div>
                </div>

                <div class="share-card-footer">
                  <span>Wisdom Islamic Organization · QVP Kids · {{ kid.year || "2026" }}</span>
                </div>
              </article>
            </div>

            <div v-else-if="kidsRegistrations.length && !kidsWithResults.length" class="empty-state pending-state">
              <div class="empty-icon empty-icon-muted">…</div>
              <h3 class="mal-text">ഫലം ഇതുവരെ പ്രസിദ്ധീകരിച്ചിട്ടില്ല</h3>
              <p class="mal-text">
                {{ kidsRegistrations.length }} കുട്ടി(കൾ) രജിസ്ട്രേഷൻ കണ്ടെത്തി. മാർക്ക് ഇനിയും പ്രസിദ്ധീകരിച്ചിട്ടില്ല.
              </p>
              <ul class="pending-list">
                <li v-for="kid in kidsRegistrations" :key="'pending-' + kid.id">{{ kid.name }}</li>
              </ul>
            </div>

            <div v-else class="empty-state">
              <div class="empty-icon empty-icon-muted">—</div>
              <h3 class="mal-text">രജിസ്ട്രേഷൻ കണ്ടെത്തിയില്ല</h3>
              <p class="mal-text">
                ഈ മൊബൈൽ നമ്പറിൽ QVP Kids രജിസ്ട്രേഷൻ ഇല്ല. രജിസ്ട്രേഷൻ ചെയ്ത നമ്പർ ഉപയോഗിച്ച് വീണ്ടും ശ്രമിക്കുക.
              </p>
              <button type="button" class="btn-secondary mal-text" @click="resetFlow">വീണ്ടും ശ്രമിക്കുക</button>
            </div>
          </template>
        </div>
      </div>

      <footer v-if="currentStep === 'mobile'" class="page-footer mal-text">
        Wisdom Islamic Organization · QVP Kids
      </footer>
    </div>
  </div>
</template>

<script>
import UserService from "@/services/user.service";

const KIDS_LOGO_URL = "https://guideportal.wisdomislam.org/uploads/image/1770639267.png";
const FINAL_EXAM_MAX = 75;
const PRELIMINARY_EXAM_MAX = 50;

export default {
  name: "QVPKidsFinalResult",
  data() {
    return {
      finalExamMax: FINAL_EXAM_MAX,
      preliminaryExamMax: PRELIMINARY_EXAM_MAX,
      kidsLogoUrl: KIDS_LOGO_URL,
      qvpLogoUrl: "/assets/img/QVP NEW LOGO HRIDAYAM.png",
      currentStep: "mobile",
      mobileCountryCode: "91",
      mobileInput: "",
      currentMobile: "",
      loading: false,
      loadingResults: false,
      alertMessage: "",
      alertClass: "",
      kidsRegistrations: [],
      countryCodeList: [
        { value: "91", label: "+91 India" },
        { value: "971", label: "+971 UAE" },
        { value: "966", label: "+966 Saudi" },
        { value: "974", label: "+974 Qatar" },
        { value: "965", label: "+965 Kuwait" },
        { value: "973", label: "+973 Bahrain" },
        { value: "968", label: "+968 Oman" },
      ],
    };
  },
  computed: {
    kidsWithResults() {
      return this.kidsRegistrations.filter((k) => this.hasAnyPublishedMark(k));
    },
  },
  methods: {
    handleKidsLogoError(event) {
      event.target.style.display = "none";
    },
    handleQvpLogoError(event) {
      event.target.style.display = "none";
    },
    showAlert(message, type = "error") {
      this.alertMessage = message;
      this.alertClass = type === "success" ? "alert-success" : "alert-error";
    },
    hideAlert() {
      this.alertMessage = "";
      this.alertClass = "";
    },
    resetFlow() {
      this.currentStep = "mobile";
      this.kidsRegistrations = [];
      this.loadingResults = false;
      this.hideAlert();
    },
    normalizeMobile(raw) {
      return String(raw || "").replace(/\D/g, "");
    },
    hasPublishedMarkValue(value) {
      return value !== null && value !== undefined && value !== "";
    },
    firstExamMark(kid) {
      if (!kid) return null;
      const m =
        kid.qvp_exam_mark ??
        kid.marks_scored ??
        kid.marks ??
        kid.mark;
      return this.hasPublishedMarkValue(m) ? m : null;
    },
    hasFirstExamMark(kid) {
      return this.firstExamMark(kid) != null;
    },
    hasFinalMark(kid) {
      return this.hasPublishedMarkValue(kid?.qvp_final_exam_mark);
    },
    getFinalRank(kid) {
      if (!kid) return null;
      const r =
        kid.qvp_final_exam_rank ??
        kid.qvp_exam_rank ??
        kid.rank ??
        kid.final_exam_rank ??
        kid.exam_rank ??
        kid.qvp_rank ??
        kid.final_rank;
      return this.hasPublishedMarkValue(r) ? r : null;
    },
    hasFinalRank(kid) {
      return this.getFinalRank(kid) != null;
    },
    formatRank(rank) {
      const n = parseInt(rank, 10);
      if (isNaN(n)) return String(rank);
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },
    getRankTier(rank) {
      const n = parseInt(rank, 10);
      if (n === 1) return "gold";
      if (n === 2) return "silver";
      if (n === 3) return "bronze";
      return "default";
    },
    getRankMedal(rank) {
      const n = parseInt(rank, 10);
      if (n === 1) return "🥇";
      if (n === 2) return "🥈";
      if (n === 3) return "🥉";
      return "🏆";
    },
    hasAnyPublishedMark(kid) {
      return this.hasFirstExamMark(kid) || this.hasFinalMark(kid);
    },
    registrationNo(kid) {
      if (!kid) return "";
      if (kid.qhls_reg_no != null && String(kid.qhls_reg_no).trim() !== "") {
        return String(kid.qhls_reg_no).trim();
      }
      return kid.id != null ? String(kid.id) : "";
    },
    formatMarkScore(mark, max) {
      if (!this.hasPublishedMarkValue(mark)) return "-";
      return `${mark}/${max}`;
    },
    getInitials(name) {
      const parts = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!parts.length) return "?";
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    async handleMobileSubmit() {
      const mobile = this.normalizeMobile(this.mobileInput);
      if (this.mobileCountryCode === "91") {
        if (mobile.length !== 10) {
          this.showAlert("ദയവായി സാധുവായ 10 അക്ക മൊബൈൽ നമ്പർ നൽകുക", "error");
          return;
        }
      } else if (mobile.length < 7 || mobile.length > 15) {
        this.showAlert("Please enter a valid mobile number (7-15 digits)", "error");
        return;
      }

      this.loading = true;
      this.hideAlert();
      this.currentMobile = mobile;
      this.loadingResults = true;
      this.kidsRegistrations = [];

      try {
        const response = await UserService.PostRequest("public/qvp/my-registration", {
          mobile,
          code: this.mobileCountryCode,
        });
        const data = response.data || {};

        if (data.status !== "success") {
          throw new Error(data.message || "എന്തോ തെറ്റ് സംഭവിച്ചു");
        }

        const list = Array.isArray(data.data) ? data.data : [];
        this.kidsRegistrations = list.filter(
          (r) => String(r.category || "").toLowerCase() === "kids"
        );
        this.currentStep = "results";

        if (!this.kidsRegistrations.length) {
          this.showAlert("ഈ നമ്പറിൽ QVP Kids രജിസ്ട്രേഷൻ കണ്ടെത്തിയില്ല.", "error");
        } else if (!this.kidsWithResults.length) {
          this.showAlert("മാർക്ക് ഇനിയും പ്രസിദ്ധീകരിച്ചിട്ടില്ല.", "error");
        } else {
          this.hideAlert();
        }
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          (err.message === "Network Error"
            ? "സെർവറുമായി ബന്ധപ്പെടാൻ കഴിഞ്ഞില്ല. ഇന്റർനെറ്റ് പരിശോധിച്ച് വീണ്ടും ശ്രമിക്കുക."
            : err.message) ||
          "ഫലം ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല. വീണ്ടും ശ്രമിക്കുക.";
        this.showAlert(msg, "error");
        this.kidsRegistrations = [];
        this.currentStep = "mobile";
      } finally {
        this.loading = false;
        this.loadingResults = false;
      }
    },
  },
};
</script>

<style>
#appCapsule.qvp-page {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  background: #f5f7f8 !important;
}

body:has(#appCapsule.qvp-page) {
  background: #f5f7f8 !important;
}
</style>

<style scoped>
.qvp-kids-final-page {
  --teal: #1a6369;
  --teal-light: #2d8a92;
  --purple: #5b4282;
  --primary: var(--teal);
  --card-bg: #ffffff;
  --text: #1f2937;
  --text-muted: #6b7280;
  --border: #e5e7eb;
  --surface: #f9fafb;

  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background: #f5f7f8;
  color: var(--text);
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}

.page-wrapper {
  max-width: 520px;
  margin: 0 auto;
  padding: 10px 14px 12px;
}

.page-header {
  margin-bottom: 14px;
}

.page-header-bar {
  position: relative;
  overflow: hidden;
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 0 16px 16px;
  box-shadow: 0 2px 8px rgba(26, 99, 105, 0.06);
}

.page-header-bar::before {
  content: "";
  display: block;
  height: 4px;
  margin: 0 -16px 14px;
  background: linear-gradient(90deg, var(--teal) 0%, #2d8a92 50%, var(--purple) 100%);
}

.page-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.page-header-badge {
  font-size: 11px;
  font-weight: 600;
  color: var(--teal);
  background: rgba(26, 99, 105, 0.08);
  padding: 5px 10px;
  border-radius: 20px;
  line-height: 1;
}

.qvp-logo-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  flex-shrink: 0;
}

.page-header-brand {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6px 0 2px;
  width: 100%;
  max-width: 100%;
}

.kids-logo-frame {
  width: 100%;
  max-width: 100%;
  height: clamp(100px, 32vw, 130px);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-header-subtitle {
  font-size: 13px;
  color: var(--text-muted);
  margin: 12px 0 0;
  padding-top: 12px;
  border-top: 1px solid var(--border);
  line-height: 1.45;
  text-align: center;
}

.qvp-logo-corner {
  width: 26px;
  height: 26px;
  object-fit: contain;
  display: block;
}

.kids-logo {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.main-card {
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid var(--border);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  padding: 14px;
}

.main-card--results {
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
}

.step-panel {
  width: 100%;
}

.lookup-form {
  margin: 0;
}

.mobile-row {
  display: flex;
  gap: 10px;
}

.code-group {
  flex: 0 0 38%;
}

.mobile-group {
  flex: 1;
}

.lookup-form .form-group {
  margin-bottom: 10px;
}

.mobile-row .form-group {
  margin-bottom: 10px;
}

.form-label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--primary);
}

.req {
  color: #dc2626;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fff;
  font-size: 16px;
  font-family: inherit;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(26, 99, 105, 0.1);
}

.btn-main {
  width: 100%;
  padding: 12px;
  margin-top: 2px;
  border: none;
  border-radius: 10px;
  background: var(--teal);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.btn-main:hover:not(:disabled) {
  background: var(--teal-light);
}

.btn-main:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.hint {
  text-align: center;
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--text-muted);
  line-height: 1.4;
}

.alert-box {
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  font-size: 13px;
}

.alert-error {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.alert-success {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.active-mobile-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 10px;
  margin-bottom: 12px;
}

.active-mobile-card .number {
  font-weight: 600;
  color: var(--text);
  font-size: 14px;
}

.link-btn {
  background: none;
  border: none;
  color: var(--teal);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.link-btn:hover {
  text-decoration: underline;
}

.loading-block {
  text-align: center;
  padding: 20px 0;
  color: var(--text-muted);
}

.loading-spinner {
  display: inline-block;
  width: 22px;
  height: 22px;
  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.loading-spinner.large {
  width: 36px;
  height: 36px;
  border-color: rgba(26, 99, 105, 0.15);
  border-top-color: var(--teal);
  margin-bottom: 12px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.result-cards {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 4px;
}

/* Result card */
.share-card {
  background: var(--card-bg);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--border);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.share-card-header {
  padding: 14px 16px;
  text-align: center;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}

.exam-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
  margin: 0 0 2px;
}

.exam-edition {
  font-size: 12px;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.4;
}

.share-card-body {
  padding: 16px;
}

.share-student {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--border);
}

.share-avatar {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--teal);
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.share-student-info {
  min-width: 0;
  text-align: left;
}

.share-name {
  font-size: 17px;
  font-weight: 600;
  color: var(--text);
  margin: 0 0 4px;
  line-height: 1.3;
}

.share-meta {
  font-size: 13px;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.4;
}

.score-section {
  margin-bottom: 12px;
}

.score-section-main {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 14px;
  padding: 18px 14px;
  background: var(--surface);
  border-radius: 12px;
  border: 1px solid var(--border);
}

.score-section-main--solo {
  justify-content: center;
}

.mark-ring {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 112px;
  height: 112px;
  border-radius: 50%;
  background: var(--card-bg);
  border: 3px solid rgba(26, 99, 105, 0.25);
  box-shadow: 0 2px 8px rgba(26, 99, 105, 0.08);
}

.mark-ring-score-wrap {
  display: flex;
  align-items: baseline;
  justify-content: center;
  line-height: 1;
  gap: 1px;
}

.mark-ring-score {
  font-size: 28px;
  font-weight: 800;
  color: var(--teal);
}

.mark-ring-max {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-muted);
}

.mark-ring-label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: var(--text-muted);
  margin-top: 6px;
  text-align: center;
  line-height: 1.25;
  max-width: 88px;
}

/* Rank medal card – vertical layout (gold / silver / bronze) */
.rank-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 88px;
  min-height: 112px;
  padding: 12px 14px;
  border-radius: 14px;
  background: linear-gradient(145deg, #f9f6fc, #f1ebfa);
  border: 2px solid rgba(91, 66, 130, 0.22);
  box-shadow: 0 3px 10px rgba(91, 66, 130, 0.1);
  text-align: center;
}

.rank-pill--gold {
  background: linear-gradient(145deg, #fff9e6, #ffe08a);
  border-color: #d4a017;
  box-shadow: 0 4px 14px rgba(212, 160, 23, 0.28);
}

.rank-pill--gold .rank-pill-label,
.rank-pill--gold .rank-pill-value {
  color: #7a5c00;
}

.rank-pill--silver {
  background: linear-gradient(145deg, #f8f9fa, #e2e6ea);
  border-color: #adb5bd;
  box-shadow: 0 4px 14px rgba(108, 117, 125, 0.22);
}

.rank-pill--silver .rank-pill-label,
.rank-pill--silver .rank-pill-value {
  color: #495057;
}

.rank-pill--bronze {
  background: linear-gradient(145deg, #fff4eb, #e8c4a8);
  border-color: #c68b59;
  box-shadow: 0 4px 14px rgba(198, 139, 89, 0.24);
}

.rank-pill--bronze .rank-pill-label,
.rank-pill--bronze .rank-pill-value {
  color: #6b4423;
}

.rank-pill--default .rank-pill-label {
  color: var(--purple);
}

.rank-pill--default .rank-pill-value {
  color: var(--teal);
}

.rank-pill-icon {
  display: block;
  font-size: 28px;
  line-height: 1;
  margin-bottom: 4px;
}

.rank-pill-label {
  display: block;
  width: 100%;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  line-height: 1.2;
  margin-bottom: 2px;
}

.rank-pill-value {
  display: block;
  width: 100%;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.15;
  white-space: nowrap;
}

.score-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border);
}

.score-row-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-muted);
}

.score-row-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--teal);
}

.score-row--prelim .score-row-value {
  color: var(--purple);
}

.share-card-footer {
  padding: 10px 16px;
  background: var(--surface);
  border-top: 1px solid var(--border);
  font-size: 11px;
  color: var(--text-muted);
  text-align: center;
}

.empty-state {
  text-align: center;
  padding: 16px 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid var(--border);
}

.empty-icon {
  font-size: 32px;
  margin-bottom: 10px;
  font-weight: 300;
  color: var(--text-muted);
}

.empty-icon-muted {
  font-size: 28px;
}

.empty-state h3 {
  font-size: 18px;
  color: var(--teal);
  margin: 0 0 10px;
}

.empty-state p {
  font-size: 14px;
  color: var(--text-muted);
  line-height: 1.6;
  margin: 0 0 16px;
}

.pending-list {
  list-style: none;
  padding: 0;
  margin: 0 0 8px;
  text-align: left;
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;
}

.pending-list li {
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 6px;
  font-size: 14px;
}

.btn-secondary {
  padding: 10px 20px;
  border: 1px solid var(--teal);
  border-radius: 10px;
  background: transparent;
  color: var(--teal);
  font-weight: 600;
  cursor: pointer;
}

.page-footer {
  text-align: center;
  padding: 10px 0 4px;
  font-size: 11px;
  color: var(--text-muted);
}

@media (max-width: 480px) {
  .page-wrapper {
    padding: 8px 10px max(12px, env(safe-area-inset-bottom));
  }

  .page-header-bar {
    padding: 0 12px 14px;
    border-radius: 12px;
  }

  .page-header-bar::before {
    margin: 0 -12px 10px;
  }

  .page-header-top {
    margin-bottom: 6px;
  }

  .page-header-brand {
    padding: 6px 0 2px;
  }

  .kids-logo-frame {
    height: clamp(104px, 34vw, 128px);
  }

  .page-header-subtitle {
    font-size: 12px;
    margin-top: 10px;
    padding-top: 10px;
  }

  .qvp-logo-wrap {
    width: 36px;
    height: 36px;
  }

  .qvp-logo-corner {
    width: 24px;
    height: 24px;
  }

  .main-card {
    padding: 12px;
  }

  .active-mobile-card {
    flex-wrap: wrap;
    gap: 8px;
  }

  .link-btn {
    min-height: 44px;
    padding: 4px 0;
  }

  .share-card-body {
    padding: 14px;
  }

  .share-name {
    font-size: 16px;
  }

  .score-section-main {
    flex-direction: row;
    gap: 10px;
    padding: 14px 10px;
  }

  .mark-ring {
    width: 100px;
    height: 100px;
  }

  .mark-ring-score {
    font-size: 24px;
  }

  .rank-pill {
    min-width: 80px;
    min-height: 100px;
    padding: 10px 12px;
  }

  .rank-pill-icon {
    font-size: 24px;
  }

  .rank-pill-value {
    font-size: 20px;
  }

  .score-row {
    padding: 12px;
  }

  .score-row-label {
    font-size: 12px;
    flex: 1;
    min-width: 0;
  }

  .score-row-value {
    font-size: 17px;
    flex-shrink: 0;
  }
}

@media (max-width: 340px) {
  .score-section-main {
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .rank-pill {
    min-height: auto;
    width: 100%;
    max-width: 200px;
    padding: 14px 16px;
  }
}
</style>
